import {
    useCreateCartItemMutation,
    useCreateSelfcareCartItemMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartItemsByIdMutation,
    useDeleteSelfcareCartItemByIdMutation,
    useDeleteSelfcareCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { CabanaWithDetails } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { updateCabana, setEditCabana, removeCabana, removeCabanas } from '.'
import { useCart } from 'store/cart-slice/hooks'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { sortDates } from 'helpers/helpers'
import { PromiseResponse } from 'helpers/types'
import {
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'
import { ErrorField, handleError } from 'helpers/errors'

export const useCabana = (
    cabanaIndex: number | null
): {
    addCabanaToCart: () => Promise<PromiseResponse>
    editCabanaInCart: () => Promise<PromiseResponse>
    removeCabanaFromCart: () => Promise<PromiseResponse>
    removeCabanasFromCart: () => Promise<PromiseResponse>
    getInitialDate: () => string
    areCabanasProcessing: boolean
} => {
    const dispatch = useAppDispatch()
    const { language, isCreatorMode, isCreatorAccommodation } = useApp()

    const { cabanas, edited } = useAppSelector((state) => state.cabana)
    const creatorCartCabanas = useAppSelector(
        (state) => state.cart.items.cabanas
    )
    const selfcareCartCabanas = useAppSelector(
        (state) => state.selfcareCart.items.cabanas
    )
    const {
        tickets: cartParkTickets,
        ticketsBundles: cartTicketsBundles,
        accommodation,
    } = useAppSelector((state) => state.cart.items)

    const {
        tickets: selfcareParkTickets,
        ticketsBundles: selfcareTicketsBundles,
        cabanas: selfcareCabanas,
    } = useAppSelector((state) => state.selfcare)

    const { detail } = accommodation || {}
    const { dateFrom } = detail || {}

    // CREATOR
    const { refetchCart: refetchCreatorCart, cartCreatorType } = useCart()
    const [postCreatorCartItem, postCreatorStatus] = useCreateCartItemMutation()
    const [deleteCreatorCartItem, deleteCreatorStatus] =
        useDeleteCartItemByIdMutation()
    const [deleteCreatorCartItems, deleteCreatorItemsStatus] =
        useDeleteCartItemsByIdMutation()

    // SELFCARE
    const { refetchSelfcareCart } = useSelfcareCart()
    const [postSelfcareCartItem, postSelfcareStatus] =
        useCreateSelfcareCartItemMutation()
    const [deleteSelfcareCartItem, deleteSelfcareStatus] =
        useDeleteSelfcareCartItemByIdMutation()
    const [deleteSelfcareCartItems, deleteSelfcareItemsStatus] =
        useDeleteSelfcareCartItemsByIdMutation()

    const cartCabanas = isCreatorMode ? creatorCartCabanas : selfcareCartCabanas

    const refetchCart = isCreatorMode ? refetchCreatorCart : refetchSelfcareCart

    const areCabanasProcessing = isCreatorMode
        ? postCreatorStatus.isLoading ||
          deleteCreatorStatus.isLoading ||
          deleteCreatorItemsStatus.isLoading
        : postSelfcareStatus.isLoading ||
          deleteSelfcareStatus.isLoading ||
          deleteSelfcareItemsStatus.isLoading

    const postCabana = async (cabana: CabanaWithDetails) => {
        const { cartItemId, productId, productType, quantity, detail } = cabana

        if (!cartItemId || !productId || !productType || !quantity) return null

        const cartItemCommandData = {
            cartItemId,
            productId,
            productType,
            quantity,
            detail,
        }

        return isCreatorMode
            ? await postCreatorCartItem({
                  createCartItemCommand: { ...cartItemCommandData },
                  cartCreatorType,
                  _locale: language,
              })
            : await postSelfcareCartItem({
                  createCartItemSelfcareCommand: {
                      ...cartItemCommandData,
                  },
                  _locale: language,
              })
    }

    const addCabanaToCart = async () => {
        if (cabanaIndex === null) return { isSuccess: false }

        try {
            handleError(await postCabana(cabanas[cabanaIndex].cabana))
            dispatch(
                updateCabana({
                    index: cabanaIndex,
                    isSubmitted: true,
                })
            )
            refetchCart(true)
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const editCabanaInCart = async () => {
        if (cabanaIndex === null || edited === null) return { isSuccess: false }

        const { cabana: originalCabana } = edited.origin
        const { cabana: modifiedCabana } = cabanas[cabanaIndex]

        if (!originalCabana.cartItemId) return { isSuccess: false }

        try {
            handleError(await handleDeleteCartItem(originalCabana.cartItemId))
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            dispatch(
                setEditCabana({ index: cabanaIndex, editState: 'editing' })
            )
            return { isSuccess: false, messages }
        }

        try {
            handleError(await postCabana(modifiedCabana))
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            dispatch(
                setEditCabana({ index: cabanaIndex, editState: 'declined' })
            )
            dispatch(removeCabana({ index: cabanaIndex }))
            refetchCart()
            return { isSuccess: false, messages }
        }

        dispatch(setEditCabana({ index: cabanaIndex, editState: 'completed' }))
        refetchCart()
        return { isSuccess: true }
    }

    const handleDeleteCartItem = async (cartItemId: string) =>
        isCreatorMode
            ? await deleteCreatorCartItem({
                  cartItemId,
                  _locale: language,
                  cartCreatorType,
              })
            : await deleteSelfcareCartItem({
                  cartItemId,
                  _locale: language,
              })

    const removeCabanaFromCart = async () => {
        if (cabanaIndex === null) return { isSuccess: false }

        const { cabana } = cabanas[cabanaIndex]
        let currentCartItemId = cabana.cartItemId

        const isDeletingCurrentlyEdited = edited && cabanaIndex === edited.index
        if (isDeletingCurrentlyEdited)
            currentCartItemId = edited.origin.cabana.cartItemId

        if (!currentCartItemId) return { isSuccess: false }

        try {
            handleError(await handleDeleteCartItem(currentCartItemId))
            isDeletingCurrentlyEdited &&
                dispatch(
                    setEditCabana({
                        index: cabanaIndex,
                        editState: 'completed',
                    })
                )
            dispatch(removeCabana({ index: cabanaIndex }))
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const removeCabanasFromCart = async () => {
        // in selfcare:
        // ignore already existed, but edited cabanas from cart
        // consider only newly added
        const editedSelfcareCabanas =
            selfcareCabanas?.map(
                (orderCabana) => orderCabana.changes?.detail.boxId
            ) || []

        const onlyNewCartCabanas = cartCabanas.filter(
            ({ detail: { boxId } }) =>
                boxId && !editedSelfcareCabanas.includes(boxId)
        )

        const cabanasIdsToRemove = onlyNewCartCabanas.map(
            (cabana) => cabana.cartItemId || ''
        )

        if (!cabanasIdsToRemove.length) {
            dispatch(removeCabanas())
            return { isSuccess: true }
        }

        try {
            const cartItemCommandData = {
                cartItemUuids: cabanasIdsToRemove,
            }
            const response = isCreatorMode
                ? await deleteCreatorCartItems({
                      _locale: language,
                      cartCreatorType,
                      deleteCartItemsByIdCommand: {
                          ...cartItemCommandData,
                      },
                  })
                : await deleteSelfcareCartItems({
                      _locale: language,
                      deleteCartItemsByIdSelfcareCommand: {
                          ...cartItemCommandData,
                      },
                  })

            handleError(response)
            dispatch(removeCabanas())
            refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const getInitialDate = () => {
        const selfcareParkTicketsDates = selfcareParkTickets
            ? selfcareParkTickets.map((ticket) => ticket.original.detail.day)
            : []
        const selfcareTicketsBundlesDates = selfcareTicketsBundles
            ? selfcareTicketsBundles.map(
                  (bundle) => bundle.original.detail.date
              )
            : []
        const selfcareParkDates = sortDates(
            Array.from(
                new Set([
                    ...selfcareParkTicketsDates,
                    ...selfcareTicketsBundlesDates,
                ])
            )
        )

        const isParkTicketInCart =
            cartParkTickets.length || cartTicketsBundles.length
        const isParkTicketInSelfcare =
            selfcareParkTickets?.length || selfcareTicketsBundles?.length

        // in std case initially use date from park tickets in other products
        if (isParkTicketInCart) {
            const sumDates = cartParkTickets.length
                ? sumTicketsDates(cartParkTickets)
                : sumTicketsBundlesDates(cartTicketsBundles)
            return sumDates.length ? sumDates[0] : ''
        }
        // when in accommodation creator, in similar way use the first day from suntago village stay
        if (isCreatorAccommodation && dateFrom) {
            return dateFrom
        }
        // in selfcare basically use park tickets dates, again
        if (isParkTicketInSelfcare) {
            return selfcareParkDates.length ? selfcareParkDates[0] : ''
        }

        return ''
    }

    return {
        addCabanaToCart,
        editCabanaInCart,
        removeCabanaFromCart,
        removeCabanasFromCart,
        areCabanasProcessing,
        getInitialDate,
    }
}
